/** @file Hierarchy view: Tree with select, rename, drag-and-drop, context menu, delete dialog and status. */
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { TreePanelFooter } from '../../forms/TreePanelFooter';
import { selectionStore } from '../../stores/selectionStore';
import { ContextMenu } from '../../ui-kit/ContextMenu';
import { Tree } from '../../ui-kit/Tree';
import { usePanelKeys } from '../usePanelKeys';
import { bindings } from './bindings';
import { dropCalls } from './dropCalls';
import { hierarchyActions as A } from './hierarchyActions';
import { parseRowId } from './parseRowId';
import { selectRow } from './selectRow';
import { useHierarchyState } from './useHierarchyState';
import '../../forms/forms.css';

/** Selecting a placeholder row also focuses its attachment in the Inspector (selection first, then focus). */
export function HierarchyTree({ doc, t }: { doc: Doc; t: Translate }) {
  const s = useHierarchyState(doc);
  const keys = usePanelKeys('hierarchy', bindings, { 'hierarchy.delete': () => { const targets = A.deleteTargets(doc, selectionStore.getState()); if (targets.length > 0) s.setDeleting(targets); } });
  const menuRow = s.menu === null ? null : parseRowId(s.menu.row);
  return (
    <div className="p2-tree-panel" data-testid="panel-hierarchy" {...keys}>
      <Tree {...s.tree} nodes={s.nodes} selected={s.selected} label={t('panels.hierarchy.tree')} renameLabel={t('panels.hierarchy.rename')}
        onSelect={(id, ctrl, shift = false) => { selectRow(doc, id, { ctrl, shift }); }}
        onRenameStart={(id) => { if (A.canRename(doc, parseRowId(id))) s.setRenaming(id); }}
        onRenameCommit={(id, name) => { s.setRenaming(null); void s.run([A.renameCall(parseRowId(id), name)]); }}
        canDrop={(a, b, where) => dropCalls(doc, s.index, a, b, where) !== null}
        onDrop={(a, b, where) => { const calls = dropCalls(doc, s.index, a, b, where); if (calls !== null) void s.run(calls); }} />
      <ContextMenu at={s.menu?.at ?? null} items={menuRow === null ? [] : A.items(doc, menuRow, t)} label={t('panels.hierarchy.menu')}
        onClose={() => { s.setMenu(null); }} onSelect={(item) => { if (s.menu !== null && menuRow !== null) s.perform(A.action(doc, menuRow, item), s.menu.row); }} />
      <TreePanelFooter doc={doc} panel={s} t={t} />
    </div>
  );
}
